// src/pages/dashboard/branch/analytics/components/PerformanceComparisonChart.tsx
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    TooltipProps,
} from "recharts";
import { FiTrendingUp, FiDollarSign, FiShoppingCart } from "react-icons/fi";
import Card from "../../../../../components/ui/Card";
import Skeleton from "../../../../../components/ui/Skeleton";
import { MenuItem } from "../../../../../utils/supabaseClient";
import { useLocalizedMenuItem } from "../../../../../hooks/useLocalizedMenuItem";

interface ChartDataPoint {
    month: string;
    [key: string]: string | number | undefined;
}

interface ItemSummary {
    item_id: string;
    totalRevenue: number;
    totalOrders: number;
    avgOrderValue: number;
}

interface PerformanceComparisonChartProps {
    chartData: ChartDataPoint[];
    itemsSummary: ItemSummary[];
    menuItemsMap: Map<string, MenuItem>;
    isLoading?: boolean;
    formatCurrency?: (value: number) => string;
    branchId: string;
}

const CHART_COLORS = [
    { main: "#10B981", light: "#A7F3D0" }, // Emerald
    { main: "#3B82F6", light: "#93C5FD" }, // Blue
    { main: "#F59E0B", light: "#FCD34D" }, // Amber
    { main: "#EF4444", light: "#FCA5A5" }, // Red
    { main: "#8B5CF6", light: "#C4B5FD" }, // Violet
];

const TooltipRow = memo(({ 
    itemId, 
    value, 
    color, 
    branchId 
}: { 
    itemId: string, 
    value: React.ReactNode, 
    color: string, 
    branchId: string 
}) => {
    const { t } = useTranslation();
    const { localizedName } = useLocalizedMenuItem(branchId, itemId);
    
    return (
        <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
                <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: color }}
                />
                <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[120px]">
                    {localizedName || t("analytics.performanceComparison.item")}
                </span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {value}
            </span>
        </div>
    );
});
TooltipRow.displayName = "TooltipRow";

const CustomTooltip: React.FC<TooltipProps<number, string> & {
    menuItemsMap: Map<string, MenuItem>;
    formatCurrency?: (value: number) => string;
    branchId: string;
}> = ({
    active,
    payload,
    label,
    menuItemsMap,
    formatCurrency,
    branchId,
}) => {
    if (!active || !payload?.length) return null;

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 min-w-[200px]"
        >
            <p className="text-sm font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
                {label}
            </p>
            <div className="space-y-2">
                {payload.map((entry, index) => {
                    const itemId = entry.dataKey?.toString().split("_").slice(1).join("_") || "";
                    const isRevenue = entry.dataKey?.toString().startsWith("revenue");
                    const formattedValue = isRevenue
                        ? formatCurrency
                            ? formatCurrency(entry.value as number)
                            : `$${(entry.value as number).toFixed(2)}`
                        : entry.value;

                    return (
                        <TooltipRow 
                            key={index}
                            itemId={itemId}
                            value={formattedValue}
                            color={entry.color || "#000"}
                            branchId={branchId}
                        />
                    );
                })}
            </div>
        </motion.div>
    );
};

const LegendItem = memo(({ 
    itemId, 
    color, 
    branchId 
}: { 
    itemId: string; 
    color: string; 
    branchId: string 
}) => {
    const { t } = useTranslation();
    const { localizedName } = useLocalizedMenuItem(branchId, itemId);
    
    return (
        <div className="flex items-center gap-2 mr-4">
             <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: color }}
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
                {localizedName || t("analytics.performanceComparison.item")}
            </span>
        </div>
    );
});
LegendItem.displayName = "LegendItem";

const CustomLegend = ({ payload, branchId }: { payload?: any[], branchId: string }) => {
    if (!payload) return null;

    return (
        <div className="flex flex-wrap justify-center gap-y-2 mt-4">
            {payload.map((entry, index) => {
                const itemId = entry.value.toString().split("_").slice(1).join("_");
                return (
                    <LegendItem 
                        key={`legend-${index}`}
                        itemId={itemId} 
                        color={entry.color} 
                        branchId={branchId} 
                    />
                );
            })}
        </div>
    );
};

const ChartSkeleton: React.FC = () => (
    <Card>
        <div className="flex items-center gap-2 mb-6">
            <Skeleton height="h-5" width="w-5" rounded="rounded" />
            <Skeleton height="h-6" width="w-48" />
        </div>
        <div className="h-[300px] relative">
            <div className="absolute inset-0 flex items-end gap-1 p-4">
                {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="flex-1 flex flex-col gap-1">
                        <div
                            className="w-full bg-emerald-100 dark:bg-emerald-900/30 rounded animate-pulse"
                            style={{ height: `${20 + Math.random() * 60}%` }}
                        />
                        <div
                            className="w-full bg-blue-100 dark:bg-blue-900/30 rounded animate-pulse"
                            style={{ height: `${10 + Math.random() * 40}%` }}
                        />
                    </div>
                ))}
            </div>
        </div>
    </Card>
);

const PerformanceComparisonChart: React.FC<PerformanceComparisonChartProps> = memo(({
    chartData,
    itemsSummary,
    menuItemsMap,
    isLoading = false,
    formatCurrency,
    branchId,
}) => {
    const { t } = useTranslation();

    if (isLoading) {
        return (
            <div className="space-y-6">
                <ChartSkeleton />
                <ChartSkeleton />
            </div>
        );
    }

    if (!chartData || chartData.length === 0 || !itemsSummary || itemsSummary.length === 0) {
        return (
            <Card>
                <div className="flex items-center gap-2 mb-4">
                    <FiTrendingUp className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t("analytics.performanceComparison.title")}
                    </h3>
                </div>
                <div className="flex flex-col items-center justify-center py-16">
                    <FiTrendingUp className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                        {t("analytics.performanceComparison.selectItemsPrompt")}
                    </p>
                </div>
            </Card>
        );
    }

    const formatRevenue = (value: number) => {
        if (formatCurrency) {
            return formatCurrency(value);
        }
        return `$${value.toFixed(2)}`;
    };

    const formatRevenueTick = (value: number) => {
        const formatted = formatRevenue(value);
        return formatted.replace(/[^\d.,-]/g, "");
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
        >
            {/* Revenue Chart */}
            <Card className="overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                            <FiDollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t("analytics.performanceComparison.monthlyRevenue")}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {t("analytics.performanceComparison.revenueTrends")}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                                {itemsSummary.map((item, idx) => (
                                    <linearGradient
                                        key={item.item_id}
                                        id={`revenueGradient_${item.item_id}`}
                                        x1="0" y1="0" x2="0" y2="1"
                                    >
                                        <stop offset="0%" stopColor={CHART_COLORS[idx % CHART_COLORS.length].main} stopOpacity={0.3} />
                                        <stop offset="100%" stopColor={CHART_COLORS[idx % CHART_COLORS.length].main} stopOpacity={0} />
                                    </linearGradient>
                                ))}
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-10" />
                            <XAxis
                                dataKey="month"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                dy={10}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                dx={-10}
                                tickFormatter={formatRevenueTick}
                            />
                            <Tooltip content={<CustomTooltip menuItemsMap={menuItemsMap} formatCurrency={formatRevenue} branchId={branchId} />} />
                            <Legend
                                content={<CustomLegend branchId={branchId} />}
                            />
                            {itemsSummary.map((item, idx) => {
                                const menuItem = menuItemsMap.get(item.item_id); // Kept for map logic if needed, but not used for name display anymore
                                return (
                                    <Area
                                        key={item.item_id}
                                        type="monotone"
                                        dataKey={`revenue_${item.item_id}`}
                                        name={`revenue_${item.item_id}`}
                                        stroke={CHART_COLORS[idx % CHART_COLORS.length].main}
                                        strokeWidth={2.5}
                                        fill={`url(#revenueGradient_${item.item_id})`}
                                        dot={false}
                                        activeDot={{
                                            r: 6,
                                            strokeWidth: 2,
                                            fill: "#fff",
                                            stroke: CHART_COLORS[idx % CHART_COLORS.length].main,
                                        }}
                                    />
                                );
                            })}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </Card>

            {/* Orders Chart */}
            <Card className="overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                            <FiShoppingCart className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t("analytics.performanceComparison.monthlyOrders")}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {t("analytics.performanceComparison.orderTrends")}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="opacity-10" />
                            <XAxis
                                dataKey="month"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                dy={10}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                dx={-10}
                            />
                            <Tooltip content={<CustomTooltip menuItemsMap={menuItemsMap} formatCurrency={formatRevenue} branchId={branchId} />} />
                            <Legend
                                content={<CustomLegend branchId={branchId} />}
                            />
                            {itemsSummary.map((item, idx) => {
                                const menuItem = menuItemsMap.get(item.item_id);
                                return (
                                    <Line
                                        key={item.item_id}
                                        type="monotone"
                                        dataKey={`orders_${item.item_id}`}
                                        name={`orders_${item.item_id}`}
                                        stroke={CHART_COLORS[idx % CHART_COLORS.length].main}
                                        strokeWidth={2.5}
                                        dot={{ r: 4, fill: "#fff", strokeWidth: 2, stroke: CHART_COLORS[idx % CHART_COLORS.length].main }}
                                        activeDot={{ r: 6, strokeWidth: 2, fill: "#fff", stroke: CHART_COLORS[idx % CHART_COLORS.length].main }}
                                    />
                                );
                            })}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>
        </motion.div>
    );
});

PerformanceComparisonChart.displayName = "PerformanceComparisonChart";

export default PerformanceComparisonChart;
